/**
 * How an integrated entity type is shown: its icon and its translated label.
 */
import { kebabCase } from 'es-toolkit';
import type { IconName } from '@insio/ui-toolkit';
import {
  LINK_DECISION_PERMISSIONS as SYNC_LINK_DECISION_PERMISSIONS,
  type Permission
} from '@insio/shared-types';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

/** Entity types with their own copy; everything else falls back to the raw name. */
export const KNOWN_ENTITY_TYPES = [
  'party',
  'cost-center',
  'job',
  'product',
  'org-unit',
  'org-unit-member',
  'requisition',
  'purchase-order',
  'goods-receipt',
  'invoice',
  'gl-posting',
  'vat-code',
  'vat-posting',
  'bank-document'
] as const;

export type KnownEntityType = (typeof KNOWN_ENTITY_TYPES)[number];

/** `Party`, `party` and `CostCenter` all name one entity: kebab-cased lower case. */
export function normalizeEntityType(entityType: string): string {
  return kebabCase(entityType.trim());
}

export function isKnownEntityType(entityType: string): entityType is KnownEntityType {
  return (KNOWN_ENTITY_TYPES as readonly string[]).includes(entityType);
}

/** The invoice entity, as `normalizeEntityType` spells it. */
export const INVOICE_ENTITY_TYPE: KnownEntityType = 'invoice';

/** Whether a connector's entity type is the invoice, however it is cased. */
export function isInvoiceEntityType(entityType: string): boolean {
  return normalizeEntityType(entityType) === INVOICE_ENTITY_TYPE;
}

/**
 * The entity's own edit or manage right a confirm, relink or set-primary needs besides
 * integrations.manage: the shared table the backend handlers use, keyed as this module spells types.
 */
export const LINK_DECISION_PERMISSIONS: Partial<Record<KnownEntityType, Permission>> =
  Object.fromEntries(
    Object.entries(SYNC_LINK_DECISION_PERMISSIONS).map(([entityType, permission]) => [
      normalizeEntityType(entityType),
      permission
    ])
  );

/** The extra right a link decision on this entity type needs, if any. */
export function linkDecisionPermission(entityType: string): Permission | undefined {
  const normalized = normalizeEntityType(entityType);
  return isKnownEntityType(normalized) ? LINK_DECISION_PERMISSIONS[normalized] : undefined;
}

/**
 * The extra right of a gated entity type as a translated verb phrase ("edit invoices"), for the
 * `permission` of the link decision messages; undefined for an ungated type.
 */
export function useLinkDecisionPermissionLabel(entityType: string): string | undefined {
  const t = useFeatureTranslation('integrations.linkDecisionPermissions');
  const normalized = normalizeEntityType(entityType);
  return linkDecisionPermission(normalized) === undefined ? undefined : t(normalized);
}

export function entityIcon(entityType: string): IconName {
  switch (normalizeEntityType(entityType)) {
    case 'party':
      return 'building';
    case 'cost-center':
      return 'wallet';
    case 'product':
      return 'grid-2';
    case 'requisition':
      return 'list-ul';
    case 'purchase-order':
      return 'bag-shopping';
    case 'goods-receipt':
      return 'box';
    case 'invoice':
      return 'file-invoice-dollar';
    case 'job':
      return 'briefcase';
    case 'org-unit':
      return 'diagram-project';
    case 'org-unit-member':
      return 'user';
    // The icons of the matching Číselníky menu items.
    case 'gl-posting':
      return 'table';
    case 'vat-code':
      return 'percent';
    case 'vat-posting':
      return 'sliders';
    case 'bank-document':
      return 'coins';
    default:
      return 'table';
  }
}

export interface EntityLabels {
  label: (entityType: string) => string;
  description: (entityType: string) => string | undefined;
}

/** Translated entity labels; an entity the copy does not know keeps its raw name. */
export function useEntityLabels(): EntityLabels {
  const tEntity = useEnumTranslation('integrationEntityType');
  return {
    label: entityType => {
      const key = normalizeEntityType(entityType);
      return isKnownEntityType(key) ? tEntity(key) : entityType;
    },
    description: entityType => {
      const key = normalizeEntityType(entityType);
      return isKnownEntityType(key) ? tEntity(`${key}Description`) : undefined;
    }
  };
}
